import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ImageUploadField from "../../components/ImageUploadField";
import SidebarLayout from "../shops/SidebarLayout";
import FreelancerSidebarLayout from "../freelancer/FreelancerSidebarLayout";
import {
  checkStorefrontHandleAvailability,
  getStorefrontSetupStatus,
  updateStorefrontSetupStatus,
  uploadStorefrontSetupHeroImage,
} from "../../services/storefrontService";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const fieldLabels = {
  displayName: "Display name",
  heroImageDesktop: "Storefront banner image",
  primaryCategories: "Primary category",
  policyTerms: "Terms and conditions",
  policyRefund: "Refund policy",
  policyCancellation: "Cancellation policy",
};

const setupStatusLabels = {
  draft: "Setup required",
  ready: "Ready",
  published: "Published",
};

const rolloutStageLabels = {
  off: "Off",
  readonly: "Read-only",
  soft: "Soft rollout",
  hard: "Hard rollout",
};

const DEFAULT_POLICY_SETTINGS = {
  cancellationWindowValue: 24,
  cancellationWindowUnit: "hours",
  refundWindowDays: 7,
  exchangeAllowed: true,
  returnCondition: "unused_unopened",
  supportContact: "",
  processingTimeBusinessDays: "1-3",
  nonRefundableItems:
    "Opened hygiene products, customized orders, and digital goods",
};

function normalizePolicySettings(value = {}) {
  const input = value && typeof value === "object" ? value : {};

  return {
    cancellationWindowValue: Number.isFinite(Number(input.cancellationWindowValue))
      ? Math.max(0, Math.floor(Number(input.cancellationWindowValue)))
      : DEFAULT_POLICY_SETTINGS.cancellationWindowValue,
    cancellationWindowUnit:
      input.cancellationWindowUnit === "days"
        ? "days"
        : DEFAULT_POLICY_SETTINGS.cancellationWindowUnit,
    refundWindowDays: Number.isFinite(Number(input.refundWindowDays))
      ? Math.max(0, Math.floor(Number(input.refundWindowDays)))
      : DEFAULT_POLICY_SETTINGS.refundWindowDays,
    exchangeAllowed:
      input.exchangeAllowed !== undefined
        ? Boolean(input.exchangeAllowed)
        : DEFAULT_POLICY_SETTINGS.exchangeAllowed,
    returnCondition:
      typeof input.returnCondition === "string" && input.returnCondition.trim()
        ? input.returnCondition.trim()
        : DEFAULT_POLICY_SETTINGS.returnCondition,
    supportContact:
      typeof input.supportContact === "string"
        ? input.supportContact.trim()
        : DEFAULT_POLICY_SETTINGS.supportContact,
    processingTimeBusinessDays:
      typeof input.processingTimeBusinessDays === "string" &&
      input.processingTimeBusinessDays.trim()
        ? input.processingTimeBusinessDays.trim()
        : DEFAULT_POLICY_SETTINGS.processingTimeBusinessDays,
    nonRefundableItems:
      typeof input.nonRefundableItems === "string" &&
      input.nonRefundableItems.trim()
        ? input.nonRefundableItems.trim()
        : DEFAULT_POLICY_SETTINGS.nonRefundableItems,
  };
}

function generatePolicyDrafts(displayName, settingsInput) {
  const settings = normalizePolicySettings(settingsInput);
  const businessName = String(displayName || "This storefront").trim();
  const cancellationWindow = `${settings.cancellationWindowValue} ${settings.cancellationWindowUnit}`;
  const exchangeText = settings.exchangeAllowed
    ? "Exchanges are available for eligible items when stock is available."
    : "Exchanges are not offered at this time.";

  const terms = `${businessName} Terms and Conditions\n\nBy placing an order with ${businessName}, you agree to these storefront terms. Product descriptions, prices, and availability may be updated at any time. Orders are confirmed only after successful payment authorization. ${businessName} reserves the right to cancel or limit orders affected by pricing errors, stock issues, or suspected fraud.\n\nProcessing and fulfillment typically occur within ${settings.processingTimeBusinessDays} business day(s). Customers are responsible for providing accurate order and pickup/delivery details. Policy updates become effective when posted to this storefront.`;

  const refund = `${businessName} Refund Policy\n\nEligible refund requests must be submitted within ${settings.refundWindowDays} day(s) of pickup or delivery. Eligible items must be ${settings.returnCondition.replace(/_/g, " ")}. Non-refundable items include: ${settings.nonRefundableItems}.\n\nRefunds are issued to the original payment method after review and approval, minus non-refundable payment processing fees charged at purchase. ${exchangeText} For damaged, incorrect, or missing items, contact ${businessName} immediately${
    settings.supportContact ? ` via ${settings.supportContact}` : ""
  } for priority support.`;

  const cancellation = `${businessName} Cancellation Policy\n\nOrder cancellations are accepted up to ${cancellationWindow} before fulfillment or pickup time. Cancellations submitted after this window may be denied or subject to partial charges when preparation has started.\n\nIf ${businessName} cancels an order due to stock or operational issues, a refund of the order total minus non-refundable payment processing fees will be issued. To request cancellation, contact ${businessName}${
    settings.supportContact ? ` via ${settings.supportContact}` : ""
  } and include your order number.`;

  return {
    terms,
    refund,
    cancellation,
  };
}

function normalizeCategoryValues(values = []) {
  return Array.from(
    new Set(
      (Array.isArray(values) ? values : [])
        .map((value) => String(value || "").trim())
        .filter(Boolean),
    ),
  );
}

function normalizeStorefrontHandleCandidate(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

function normalizeHeroImageValues(values = []) {
  return Array.from(
    new Set(
      (Array.isArray(values) ? values : [])
        .map((value) => String(value || "").trim())
        .filter(Boolean),
    ),
  ).slice(0, 3);
}

function areSameStringSets(left = [], right = []) {
  const leftSet = new Set(
    (left || []).map((value) => String(value).toLowerCase()),
  );
  const rightSet = new Set(
    (right || []).map((value) => String(value).toLowerCase()),
  );
  if (leftSet.size !== rightSet.size) return false;
  for (const value of leftSet) {
    if (!rightSet.has(value)) return false;
  }
  return true;
}

function SetupChecklist({ statusData }) {
  const requiredFields = statusData?.requiredFields || {};

  return (
    <div style={{ display: "grid", gap: "10px" }}>
      {Object.entries(requiredFields).map(([key, done]) => (
        <div
          key={key}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "12px 14px",
            borderRadius: "10px",
            border: `1px solid ${done ? "#86efac" : "#fecaca"}`,
            background: done ? "#f0fdf4" : "#fef2f2",
          }}
        >
          <span style={{ fontSize: "14px", color: "#0f172a", fontWeight: 500 }}>
            {fieldLabels[key] || key}
          </span>
          <span
            style={{
              fontSize: "12px",
              fontWeight: 700,
              color: done ? "#166534" : "#991b1b",
            }}
          >
            {done ? "DONE" : "MISSING"}
          </span>
        </div>
      ))}
    </div>
  );
}

function StorefrontSetupPage({ roleType }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [statusData, setStatusData] = useState(null);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [selectedPrimaryCategories, setSelectedPrimaryCategories] = useState(
    [],
  );
  const [savedPrimaryCategories, setSavedPrimaryCategories] = useState([]);
  const [storefrontDisplayName, setStorefrontDisplayName] = useState("");
  const [savedStorefrontDisplayName, setSavedStorefrontDisplayName] =
    useState("");
  const [storefrontHandle, setStorefrontHandle] = useState("");
  const [savedStorefrontHandle, setSavedStorefrontHandle] = useState("");
  const [handleStatus, setHandleStatus] = useState({
    tone: "neutral",
    message: "",
    normalizedHandle: "",
  });
  const [checkingHandle, setCheckingHandle] = useState(false);
  const [selectedHeroImageDesktop, setSelectedHeroImageDesktop] = useState("");
  const [savedHeroImageDesktop, setSavedHeroImageDesktop] = useState("");
  const [heroImages, setHeroImages] = useState([]);
  const [heroImageFile, setHeroImageFile] = useState(null);
  const [uploadingHeroImage, setUploadingHeroImage] = useState(false);
  const [policySettings, setPolicySettings] = useState(DEFAULT_POLICY_SETTINGS);
  const [policyTerms, setPolicyTerms] = useState("");
  const [policyRefund, setPolicyRefund] = useState("");
  const [policyCancellation, setPolicyCancellation] = useState("");
  const [savingPolicies, setSavingPolicies] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [saveMsg, setSaveMsg] = useState("");

  const isBusiness = roleType === "R1";

  const publicSiteOrigin = useMemo(() => {
    if (typeof window !== "undefined" && window.location?.origin) {
      return window.location.origin;
    }
    return "https://www.ogestyle.com";
  }, []);

  const productBasePath = useMemo(() => {
    if (roleType === "R7") return "/dashboard/booth-renter/products";
    if (roleType === "R4") return "/dashboard/freelancer/products";
    return "/dashboard/products";
  }, [roleType]);

  useEffect(() => {
    let mounted = true;

    const fetchStatusAndCategories = async () => {
      try {
        const results = await Promise.allSettled([
          getStorefrontSetupStatus(),
          API_BASE_URL
            ? fetch(`${API_BASE_URL}/api/platform-settings/product-categories`)
            : Promise.resolve(null),
        ]);

        if (!mounted) return;

        const statusResult = results[0];
        const categoriesResult = results[1];

        if (
          statusResult.status === "fulfilled" &&
          statusResult.value?.success
        ) {
          const normalized = normalizeCategoryValues(
            statusResult.value.fields?.primaryCategories,
          );
          const heroImageDesktop = String(
            statusResult.value.fields?.heroImageDesktop || "",
          ).trim();
          const normalizedHeroImages = normalizeHeroImageValues(
            statusResult.value.fields?.heroImages,
          );
          const loadedPolicyTerms = String(
            statusResult.value.fields?.policyTerms || "",
          ).trim();
          const loadedPolicyRefund = String(
            statusResult.value.fields?.policyRefund || "",
          ).trim();
          const loadedPolicyCancellation = String(
            statusResult.value.fields?.policyCancellation || "",
          ).trim();
          const loadedPolicySettings = normalizePolicySettings(
            statusResult.value.fields?.policySettings,
          );
          const loadedStorefrontDisplayName = String(
            statusResult.value.fields?.storefrontDisplayName || "",
          ).trim();
          const loadedStorefrontHandle = String(
            statusResult.value.fields?.storefrontHandle || "",
          ).trim();
          const resolvedHeroImages =
            normalizedHeroImages.length > 0
              ? normalizedHeroImages
              : heroImageDesktop
                ? [heroImageDesktop]
                : [];

          const seededDrafts =
            !loadedPolicyTerms &&
            !loadedPolicyRefund &&
            !loadedPolicyCancellation
              ? generatePolicyDrafts(
                  statusResult.value.fields?.displayName,
                  loadedPolicySettings,
                )
              : null;

          setStatusData(statusResult.value);
          setSelectedPrimaryCategories(normalized);
          setSavedPrimaryCategories(normalized);
          setStorefrontDisplayName(loadedStorefrontDisplayName);
          setSavedStorefrontDisplayName(loadedStorefrontDisplayName);
          setStorefrontHandle(loadedStorefrontHandle);
          setSavedStorefrontHandle(loadedStorefrontHandle);
          setSelectedHeroImageDesktop(heroImageDesktop);
          setSavedHeroImageDesktop(heroImageDesktop);
          setHeroImages(resolvedHeroImages);
          setPolicySettings(loadedPolicySettings);
          setPolicyTerms(loadedPolicyTerms || seededDrafts?.terms || "");
          setPolicyRefund(loadedPolicyRefund || seededDrafts?.refund || "");
          setPolicyCancellation(
            loadedPolicyCancellation || seededDrafts?.cancellation || "",
          );
        } else {
          setErrorMsg("Unable to load storefront setup status");
        }

        if (
          categoriesResult.status === "fulfilled" &&
          categoriesResult.value &&
          categoriesResult.value.ok
        ) {
          const categoryData = await categoriesResult.value.json();
          setAvailableCategories(
            Array.isArray(categoryData) ? categoryData : [],
          );
        }
      } catch (error) {
        setErrorMsg("Unable to load storefront setup status");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchStatusAndCategories();

    return () => {
      mounted = false;
    };
  }, []);

  const hasUnsavedCategoryChanges =
    !loading &&
    !areSameStringSets(selectedPrimaryCategories, savedPrimaryCategories);
  const hasUnsavedIdentityChanges =
    !loading &&
    (storefrontDisplayName.trim() !== savedStorefrontDisplayName.trim() ||
      normalizeStorefrontHandleCandidate(storefrontHandle) !==
        normalizeStorefrontHandleCandidate(savedStorefrontHandle));
  const hasUnsavedChanges =
    hasUnsavedCategoryChanges || hasUnsavedIdentityChanges;

  const normalizedSavedHandle = useMemo(
    () => normalizeStorefrontHandleCandidate(savedStorefrontHandle),
    [savedStorefrontHandle],
  );

  const storefrontHandleCooldownDays = useMemo(() => {
    const value = Number(statusData?.storefrontHandleCooldown?.cooldownDays || 0);
    return Number.isFinite(value) && value > 0 ? value : null;
  }, [statusData?.storefrontHandleCooldown?.cooldownDays]);

  const storefrontHandleNextAllowedLabel = useMemo(() => {
    const rawValue = String(
      statusData?.storefrontHandleCooldown?.nextAllowedAt || "",
    ).trim();
    if (!rawValue) return null;

    const parsedDate = new Date(rawValue);
    if (Number.isNaN(parsedDate.getTime())) return null;
    return parsedDate.toLocaleString();
  }, [statusData?.storefrontHandleCooldown?.nextAllowedAt]);

  const storefrontPreviewPath = useMemo(() => {
    if (!handleStatus.normalizedHandle) return "";
    if (isBusiness) {
      return `/shop/${handleStatus.normalizedHandle}/your-location-slug/products`;
    }
    return `/shop/${handleStatus.normalizedHandle}/products`;
  }, [handleStatus.normalizedHandle, isBusiness]);

  const storefrontPreviewUrl = useMemo(() => {
    if (!storefrontPreviewPath) return "";
    return `${publicSiteOrigin}${storefrontPreviewPath}`;
  }, [publicSiteOrigin, storefrontPreviewPath]);
  const normalizedDraftHandle = useMemo(() => {
    const manualCandidate = normalizeStorefrontHandleCandidate(storefrontHandle);
    if (manualCandidate) {
      return manualCandidate;
    }

    return normalizeStorefrontHandleCandidate(storefrontDisplayName);
  }, [storefrontDisplayName, storefrontHandle]);

  useEffect(() => {
    let cancelled = false;

    const runHandleCheck = async () => {
      if (!normalizedDraftHandle) {
        setCheckingHandle(false);
        setHandleStatus({
          tone: "neutral",
          message:
            "Add a storefront alias to generate a handle, or enter a custom handle.",
          normalizedHandle: "",
        });
        return;
      }

      if (normalizedDraftHandle === normalizedSavedHandle) {
        setCheckingHandle(false);
        setHandleStatus({
          tone: "neutral",
          message: "Current storefront handle",
          normalizedHandle: normalizedDraftHandle,
        });
        return;
      }

      setCheckingHandle(true);
      setHandleStatus((prev) => ({
        ...prev,
        tone: "neutral",
        message: "Checking handle availability...",
        normalizedHandle: normalizedDraftHandle,
      }));

      try {
        const response = await checkStorefrontHandleAvailability(
          storefrontHandle.trim() || storefrontDisplayName.trim(),
        );

        if (cancelled) return;

        setHandleStatus({
          tone: response?.available ? "success" : "error",
          message: response?.available
            ? "Handle is available"
            : response?.message || "Handle is unavailable",
          normalizedHandle:
            String(response?.normalizedHandle || normalizedDraftHandle).trim(),
        });
      } catch (error) {
        if (cancelled) return;

        const responseData = error?.response?.data || {};
        setHandleStatus({
          tone: "error",
          message:
            responseData.message || "Unable to check storefront handle availability.",
          normalizedHandle:
            String(responseData.normalizedHandle || normalizedDraftHandle).trim(),
        });
      } finally {
        if (!cancelled) {
          setCheckingHandle(false);
        }
      }
    };

    const timeoutId = setTimeout(runHandleCheck, 250);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [
    normalizedDraftHandle,
    normalizedSavedHandle,
    storefrontDisplayName,
    storefrontHandle,
  ]);

  const togglePrimaryCategory = (value) => {
    const normalizedValue = String(value || "").trim();
    if (!normalizedValue) return;

    setSelectedPrimaryCategories((prev) => {
      const exists = prev.some(
        (entry) =>
          String(entry).toLowerCase() === normalizedValue.toLowerCase(),
      );
      if (exists) {
        return prev.filter(
          (entry) =>
            String(entry).toLowerCase() !== normalizedValue.toLowerCase(),
        );
      }
      return [...prev, normalizedValue];
    });
    setSaveMsg("");
  };

  const handleSaveSetup = async () => {
    if (selectedPrimaryCategories.length === 0) {
      setErrorMsg("Please select at least one primary category before saving.");
      return;
    }

    setSaving(true);
    setErrorMsg("");
    setSaveMsg("");

    try {
      const payload = {
        primaryCategories: selectedPrimaryCategories,
      };
      const trimmedStorefrontDisplayName = storefrontDisplayName.trim();
      const trimmedStorefrontHandle = storefrontHandle.trim();

      if (
        trimmedStorefrontDisplayName &&
        trimmedStorefrontDisplayName !== savedStorefrontDisplayName.trim()
      ) {
        payload.storefrontDisplayName = trimmedStorefrontDisplayName;
      }

      if (
        trimmedStorefrontHandle &&
        normalizeStorefrontHandleCandidate(trimmedStorefrontHandle) !==
          normalizedSavedHandle
      ) {
        payload.storefrontHandle = trimmedStorefrontHandle;
      }

      const response = await updateStorefrontSetupStatus(payload);

      if (response?.success) {
        const normalized = normalizeCategoryValues(
          response.fields?.primaryCategories,
        );
        const heroImageDesktop = String(
          response.fields?.heroImageDesktop || "",
        ).trim();
        const nextStorefrontDisplayName = String(
          response.fields?.storefrontDisplayName || "",
        ).trim();
        const nextStorefrontHandle = String(
          response.fields?.storefrontHandle || "",
        ).trim();
        setStatusData(response);
        setSelectedPrimaryCategories(normalized);
        setSavedPrimaryCategories(normalized);
        setStorefrontDisplayName(nextStorefrontDisplayName);
        setSavedStorefrontDisplayName(nextStorefrontDisplayName);
        setStorefrontHandle(nextStorefrontHandle);
        setSavedStorefrontHandle(nextStorefrontHandle);
        setSelectedHeroImageDesktop(heroImageDesktop);
        setSavedHeroImageDesktop(heroImageDesktop);
        setSaveMsg("Storefront setup saved.");
      } else {
        setErrorMsg("Unable to save storefront setup.");
      }
    } catch (error) {
      setErrorMsg(
        error?.response?.data?.message || "Unable to save storefront setup.",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleHeroImageUpload = async () => {
    if (!heroImageFile) {
      setErrorMsg("Please choose an image file first.");
      return;
    }

    if (heroImages.length >= 3) {
      setErrorMsg(
        "You already have 3 storefront banner images. Remove one to upload a replacement.",
      );
      return;
    }

    setUploadingHeroImage(true);
    setErrorMsg("");
    setSaveMsg("");

    try {
      const response = await uploadStorefrontSetupHeroImage(heroImageFile);
      const imageUrl = String(response?.imageUrl || "").trim();
      const nextHeroImages = normalizeHeroImageValues(response?.heroImages);
      if (!imageUrl) {
        setErrorMsg("Upload succeeded but image URL was missing.");
      } else {
        const resolvedHeroImages =
          nextHeroImages.length > 0
            ? nextHeroImages
            : normalizeHeroImageValues([...heroImages, imageUrl]);
        setHeroImages(resolvedHeroImages);
        setSelectedHeroImageDesktop(resolvedHeroImages[0] || imageUrl);
        setSavedHeroImageDesktop(resolvedHeroImages[0] || imageUrl);
        setStatusData((prev) =>
          prev
            ? {
                ...prev,
                fields: {
                  ...(prev.fields || {}),
                  heroImageDesktop: resolvedHeroImages[0] || imageUrl,
                  heroImages: resolvedHeroImages,
                },
              }
            : prev,
        );
        setSaveMsg("Storefront banner image uploaded and saved.");
      }
      setHeroImageFile(null);
    } catch (error) {
      const errorCode = String(error?.response?.data?.error || "").trim();
      if (errorCode === "hero_image_limit_reached") {
        setErrorMsg(
          "You already have 3 storefront banner images. Remove one image to upload a new one.",
        );
      } else {
        setErrorMsg(
          error?.response?.data?.message ||
            "Failed to upload storefront banner image.",
        );
      }
    } finally {
      setUploadingHeroImage(false);
    }
  };

  const handleRemoveHeroImage = async (indexToRemove) => {
    if (!Number.isInteger(indexToRemove) || indexToRemove < 0) return;

    const nextHeroImages = heroImages.filter((_, index) => index !== indexToRemove);

    setSaving(true);
    setErrorMsg("");
    setSaveMsg("");

    try {
      const response = await updateStorefrontSetupStatus({
        heroImages: nextHeroImages,
      });

      if (!response?.success) {
        setErrorMsg("Unable to remove storefront banner image.");
        return;
      }

      const normalizedHeroImages = normalizeHeroImageValues(
        response.fields?.heroImages,
      );
      const heroImageDesktop = String(
        response.fields?.heroImageDesktop || "",
      ).trim();
      const resolvedHeroImages =
        normalizedHeroImages.length > 0
          ? normalizedHeroImages
          : heroImageDesktop
            ? [heroImageDesktop]
            : [];

      setHeroImages(resolvedHeroImages);
      setSelectedHeroImageDesktop(heroImageDesktop);
      setSavedHeroImageDesktop(heroImageDesktop);
      setStatusData(response);
      setSaveMsg(
        resolvedHeroImages.length < 3
          ? "Banner image removed. You can now upload a replacement."
          : "Banner image removed.",
      );
    } catch (error) {
      setErrorMsg(
        error?.response?.data?.message ||
          "Unable to remove storefront banner image.",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSetPrimaryHeroImage = async (indexToPromote) => {
    if (!Number.isInteger(indexToPromote) || indexToPromote <= 0) return;
    if (indexToPromote >= heroImages.length) return;

    const selectedImage = heroImages[indexToPromote];
    const nextHeroImages = normalizeHeroImageValues([
      selectedImage,
      ...heroImages.filter((_, index) => index !== indexToPromote),
    ]);

    setSaving(true);
    setErrorMsg("");
    setSaveMsg("");

    try {
      const response = await updateStorefrontSetupStatus({
        heroImages: nextHeroImages,
      });

      if (!response?.success) {
        setErrorMsg("Unable to set primary storefront banner image.");
        return;
      }

      const normalizedHeroImages = normalizeHeroImageValues(
        response.fields?.heroImages,
      );
      const heroImageDesktop = String(
        response.fields?.heroImageDesktop || "",
      ).trim();
      const resolvedHeroImages =
        normalizedHeroImages.length > 0
          ? normalizedHeroImages
          : heroImageDesktop
            ? [heroImageDesktop]
            : [];

      setHeroImages(resolvedHeroImages);
      setSelectedHeroImageDesktop(heroImageDesktop);
      setSavedHeroImageDesktop(heroImageDesktop);
      setStatusData(response);
      setSaveMsg("Primary storefront banner image updated.");
    } catch (error) {
      setErrorMsg(
        error?.response?.data?.message ||
          "Unable to set primary storefront banner image.",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleGeneratePolicyDrafts = () => {
    const drafts = generatePolicyDrafts(
      statusData?.fields?.displayName,
      policySettings,
    );
    setPolicyTerms(drafts.terms);
    setPolicyRefund(drafts.refund);
    setPolicyCancellation(drafts.cancellation);
    setSaveMsg("Policy drafts generated. Review and save when ready.");
  };

  const handleSavePolicies = async () => {
    if (!policyTerms || !policyRefund || !policyCancellation) {
      setErrorMsg(
        "Terms, refund, and cancellation policy text are all required.",
      );
      return;
    }

    setSavingPolicies(true);
    setErrorMsg("");
    setSaveMsg("");

    try {
      const response = await updateStorefrontSetupStatus({
        policyTerms,
        policyRefund,
        policyCancellation,
        policySettings,
      });

      if (!response?.success) {
        setErrorMsg("Unable to save storefront policy documents.");
        return;
      }

      setStatusData(response);
      setSaveMsg("Storefront policy documents saved.");
    } catch (error) {
      setErrorMsg(
        error?.response?.data?.message ||
          "Unable to save storefront policy documents.",
      );
    } finally {
      setSavingPolicies(false);
    }
  };

  const content = (
    <div
      className="storefront-setup-page"
      style={{ maxWidth: "900px", margin: "0 auto", padding: "20px" }}
    >
      <button
        type="button"
        onClick={() => navigate(productBasePath)}
        style={{
          marginBottom: "16px",
          background: "none",
          border: "none",
          color: "#0f172a",
          fontSize: "14px",
          fontWeight: 600,
          cursor: "pointer",
          padding: 0,
        }}
      >
        {"<- Back to Products"}
      </button>

      <div
        style={{
          background: "#ffffff",
          border: "1px solid #e2e8f0",
          borderRadius: "12px",
          padding: "20px",
          marginBottom: "18px",
        }}
      >
        <h2 style={{ margin: "0 0 8px 0", fontSize: "24px", color: "#0f172a" }}>
          Storefront Setup
        </h2>
      </div>

      {loading && (
        <div
          style={{
            background: "#ffffff",
            border: "1px solid #e2e8f0",
            borderRadius: "12px",
            padding: "18px",
          }}
        >
          <p style={{ margin: 0, color: "#334155" }}>Loading setup status...</p>
        </div>
      )}

      {!loading && errorMsg && !statusData && (
        <div
          style={{
            background: "#fff3f3",
            border: "1px solid #fecaca",
            borderRadius: "12px",
            padding: "16px",
          }}
        >
          <p style={{ margin: 0, color: "#991b1b" }}>{errorMsg}</p>
        </div>
      )}

      {!loading && statusData && errorMsg && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Storefront setup error"
          onClick={() => setErrorMsg("")}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15, 23, 42, 0.55)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "16px",
          }}
        >
          <div
            onClick={(event) => event.stopPropagation()}
            style={{
              width: "100%",
              maxWidth: "520px",
              background: "#ffffff",
              borderRadius: "14px",
              border: "1px solid #fee2e2",
              boxShadow: "0 24px 48px rgba(15, 23, 42, 0.28)",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "14px 16px",
                borderBottom: "1px solid #fee2e2",
                background: "#fff1f2",
              }}
            >
              <h3
                style={{
                  margin: 0,
                  fontSize: "16px",
                  fontWeight: 700,
                  color: "#9f1239",
                }}
              >
                Unable to update storefront setup
              </h3>
              <button
                type="button"
                onClick={() => setErrorMsg("")}
                style={{
                  border: "none",
                  background: "transparent",
                  color: "#9f1239",
                  fontSize: "18px",
                  lineHeight: 1,
                  cursor: "pointer",
                }}
                aria-label="Close error message"
              >
                ×
              </button>
            </div>

            <div style={{ padding: "16px" }}>
              <p
                style={{
                  margin: 0,
                  color: "#881337",
                  fontSize: "14px",
                  lineHeight: 1.5,
                }}
              >
                {errorMsg}
              </p>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                padding: "0 16px 16px",
              }}
            >
              <button
                type="button"
                onClick={() => setErrorMsg("")}
                style={{
                  border: "1px solid #e11d48",
                  borderRadius: "8px",
                  background: "#e11d48",
                  color: "#ffffff",
                  padding: "8px 14px",
                  fontSize: "13px",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {!loading && statusData && (
        <>
          {statusData.onboardingStage === "off" && (
            <div
              style={{
                background: "#ffffff",
                border: "1px solid #e2e8f0",
                borderRadius: "12px",
                padding: "18px",
                marginBottom: "16px",
              }}
            >
              <p style={{ margin: 0, color: "#475569", fontSize: "14px" }}>
                Storefront onboarding rollout is currently OFF. Existing product
                workflows continue unchanged, but you can preconfigure setup
                values here.
              </p>
            </div>
          )}

          <div
            style={{
              background: "#ffffff",
              border: "1px solid #e2e8f0",
              borderRadius: "12px",
              padding: "18px",
              marginBottom: "16px",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: "12px",
                flexWrap: "wrap",
              }}
            >
              <div>
                <div
                  style={{
                    color: "#0f172a",
                    fontWeight: 700,
                    fontSize: "15px",
                  }}
                >
                  Status: {setupStatusLabels[statusData.setupStatus] || statusData.setupStatus}
                </div>
                <div
                  style={{
                    color: "#475569",
                    fontSize: "13px",
                    marginTop: "4px",
                  }}
                >
                  Completion: {statusData.completionPercent}%
                </div>
                <div
                  style={{
                    color: "#475569",
                    fontSize: "13px",
                    marginTop: "4px",
                  }}
                >
                  Rollout stage: {rolloutStageLabels[statusData.onboardingStage] || statusData.onboardingStage || "Hard rollout"}
                </div>
              </div>

              <button
                type="button"
                onClick={handleSaveSetup}
                disabled={
                  saving ||
                  !hasUnsavedChanges ||
                  selectedPrimaryCategories.length === 0
                }
                style={{
                  border: "1px solid #1d4ed8",
                  background:
                    saving ||
                    !hasUnsavedChanges ||
                    selectedPrimaryCategories.length === 0
                      ? "#bfdbfe"
                      : "#2563eb",
                  color: "#ffffff",
                  borderRadius: "8px",
                  padding: "8px 12px",
                  fontSize: "12px",
                  fontWeight: 600,
                  cursor:
                    saving ||
                    !hasUnsavedChanges ||
                    selectedPrimaryCategories.length === 0
                      ? "not-allowed"
                      : "pointer",
                }}
              >
                {saving ? "Saving..." : "Save setup"}
              </button>
            </div>
            {saveMsg && (
              <p
                style={{
                  margin: "10px 0 0 0",
                  color: "#166534",
                  fontSize: "13px",
                  fontWeight: 600,
                }}
              >
                {saveMsg}
              </p>
            )}

            {Array.isArray(statusData.notifications) &&
              statusData.notifications.length > 0 && (
                <div style={{ marginTop: "10px", display: "grid", gap: "6px" }}>
                  {statusData.notifications.slice(0, 2).map((note, index) => (
                    <div
                      key={`${note.title || "notification"}-${index}`}
                      style={{
                        fontSize: "13px",
                        color: note.level === "error" ? "#991b1b" : "#334155",
                      }}
                    >
                      <strong>{note.title}:</strong> {note.message}
                    </div>
                  ))}
                </div>
              )}
          </div>

          <div
            style={{
              background: "#ffffff",
              border: "1px solid #e2e8f0",
              borderRadius: "12px",
              padding: "18px",
              marginBottom: "16px",
            }}
          >
            <h3
              style={{
                margin: "0 0 12px 0",
                fontSize: "16px",
                color: "#0f172a",
              }}
            >
              Storefront Identity
            </h3>
            <p
              style={{
                margin: "0 0 14px 0",
                color: "#475569",
                fontSize: "13px",
              }}
            >
              Set the customer-facing store identity for this seller profile. Leave the custom
              handle blank if you want it generated from your store name.
            </p>

            {storefrontHandleCooldownDays && (
              <div
                style={{
                  marginBottom: "14px",
                  background: "#fffbeb",
                  border: "1px solid #fcd34d",
                  borderRadius: "10px",
                  padding: "10px 12px",
                }}
              >
                <p
                  style={{
                    margin: 0,
                    color: "#92400e",
                    fontSize: "13px",
                    lineHeight: 1.45,
                    fontWeight: 600,
                  }}
                >
                  Important before setup: Your Store Name (shop name) can only be changed every {storefrontHandleCooldownDays} day(s) after it is updated.
                  {statusData?.storefrontHandleCooldown?.isBlocked && storefrontHandleNextAllowedLabel
                    ? ` Next allowed update: ${storefrontHandleNextAllowedLabel}.`
                    : ""}
                </p>
              </div>
            )}

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                gap: "12px",
              }}
            >
              <label style={{ display: "grid", gap: "6px", fontSize: "12px" }}>
                Your Store Name
                <input
                  type="text"
                  maxLength={120}
                  value={storefrontDisplayName}
                  onChange={(event) => {
                    setStorefrontDisplayName(event.target.value);
                    setSaveMsg("");
                  }}
                  placeholder={statusData.fields?.displayName || "Enter your store name"}
                  style={{ border: "1px solid #cbd5e1", borderRadius: "6px", padding: "8px" }}
                />
                <span style={{ color: "#64748b" }}>
                  This is shown to customers across your public storefront and product pages.
                </span>
              </label>

              <label style={{ display: "grid", gap: "6px", fontSize: "12px" }}>
                Custom storefront handle
                <input
                  type="text"
                  maxLength={120}
                  value={storefrontHandle}
                  onChange={(event) => {
                    setStorefrontHandle(event.target.value);
                    setSaveMsg("");
                  }}
                  placeholder="Leave blank to auto-generate from alias"
                  style={{ border: "1px solid #cbd5e1", borderRadius: "6px", padding: "8px" }}
                />
                <span style={{ color: "#64748b" }}>
                  Lowercase letters and numbers only after normalization.
                </span>
              </label>
            </div>

            <div
              style={{
                marginTop: "12px",
                display: "grid",
                gap: "6px",
                fontSize: "13px",
              }}
            >
              <div style={{ color: "#334155" }}>
                Preview handle: {handleStatus.normalizedHandle || "Not ready"}
              </div>
              <div
                style={{
                  color:
                    handleStatus.tone === "success"
                      ? "#166534"
                      : handleStatus.tone === "error"
                        ? "#991b1b"
                        : "#475569",
                  fontWeight: handleStatus.tone === "neutral" ? 500 : 600,
                }}
              >
                {checkingHandle ? "Checking handle availability..." : handleStatus.message}
              </div>
              {storefrontHandleCooldownDays && (
                <div style={{ color: "#475569" }}>
                  Handle change policy: You can change your storefront handle every {storefrontHandleCooldownDays} day(s).
                  {statusData?.storefrontHandleCooldown?.isBlocked && storefrontHandleNextAllowedLabel
                    ? ` Next available change: ${storefrontHandleNextAllowedLabel}.`
                    : ""}
                </div>
              )}
              <div style={{ color: "#475569" }}>
                Public storefront URL preview: {storefrontPreviewUrl || "Available once your alias or custom handle is set"}
              </div>
              {isBusiness && handleStatus.normalizedHandle && (
                <div style={{ color: "#64748b", fontSize: "12px" }}>
                  Replace <strong>your-location-slug</strong> with each storefront location slug.
                </div>
              )}
            </div>
          </div>

          <div
            style={{
              background: "#ffffff",
              border: "1px solid #e2e8f0",
              borderRadius: "12px",
              padding: "18px",
              marginBottom: "16px",
            }}
          >
            <h3
              style={{
                margin: "0 0 12px 0",
                fontSize: "16px",
                color: "#0f172a",
              }}
            >
              Required Checklist
            </h3>
            <SetupChecklist statusData={statusData} />
          </div>

          <div
            style={{
              background: "#ffffff",
              border: "1px solid #e2e8f0",
              borderRadius: "12px",
              padding: "18px",
            }}
          >
            <h3
              style={{
                margin: "0 0 12px 0",
                fontSize: "16px",
                color: "#0f172a",
              }}
            >
              Current Data Snapshot
            </h3>
            <div
              style={{
                display: "grid",
                gap: "8px",
                color: "#334155",
                fontSize: "13px",
              }}
            >
              <div>
                Display name: {statusData.fields?.displayName || "Not set"}
              </div>
              <div>
                Storefront alias: {statusData.fields?.storefrontDisplayName || "Not set"}
              </div>
              <div>
                Storefront handle: {statusData.fields?.storefrontHandle || "Not set"}
              </div>
              <div>
                Storefront banner image:{" "}
                {statusData.fields?.heroImageDesktop ? "Available" : "Not set"}
              </div>
              <div>
                Primary categories:{" "}
                {Array.isArray(statusData.fields?.primaryCategories) &&
                statusData.fields.primaryCategories.length > 0
                  ? statusData.fields.primaryCategories.join(", ")
                  : "Not set"}
              </div>
            </div>
          </div>

          <div
            style={{
              background: "#ffffff",
              border: "1px solid #e2e8f0",
              borderRadius: "12px",
              padding: "18px",
              marginTop: "16px",
            }}
          >
            <h3
              style={{
                margin: "0 0 12px 0",
                fontSize: "16px",
                color: "#0f172a",
              }}
            >
              Policy Builder (Terms, Refund, Cancellation)
            </h3>
            <p
              style={{
                margin: "0 0 12px 0",
                color: "#475569",
                fontSize: "13px",
              }}
            >
              Use this guided builder to generate policy drafts quickly. Fill in
              your specifics, generate drafts, review the text, then save.
            </p>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                gap: "10px",
                marginBottom: "12px",
              }}
            >
              <label style={{ display: "grid", gap: "6px", fontSize: "12px" }}>
                Cancellation window value
                <input
                  type="number"
                  min="0"
                  value={policySettings.cancellationWindowValue}
                  onChange={(event) =>
                    setPolicySettings((prev) => ({
                      ...prev,
                      cancellationWindowValue: Math.max(
                        0,
                        Number(event.target.value || 0),
                      ),
                    }))
                  }
                  style={{ border: "1px solid #cbd5e1", borderRadius: "6px", padding: "8px" }}
                />
              </label>

              <label style={{ display: "grid", gap: "6px", fontSize: "12px" }}>
                Cancellation window unit
                <select
                  value={policySettings.cancellationWindowUnit}
                  onChange={(event) =>
                    setPolicySettings((prev) => ({
                      ...prev,
                      cancellationWindowUnit: event.target.value === "days" ? "days" : "hours",
                    }))
                  }
                  style={{ border: "1px solid #cbd5e1", borderRadius: "6px", padding: "8px" }}
                >
                  <option value="hours">Hours</option>
                  <option value="days">Days</option>
                </select>
              </label>

              <label style={{ display: "grid", gap: "6px", fontSize: "12px" }}>
                Refund window (days)
                <input
                  type="number"
                  min="0"
                  value={policySettings.refundWindowDays}
                  onChange={(event) =>
                    setPolicySettings((prev) => ({
                      ...prev,
                      refundWindowDays: Math.max(0, Number(event.target.value || 0)),
                    }))
                  }
                  style={{ border: "1px solid #cbd5e1", borderRadius: "6px", padding: "8px" }}
                />
              </label>

              <label style={{ display: "grid", gap: "6px", fontSize: "12px" }}>
                Return condition
                <select
                  value={policySettings.returnCondition}
                  onChange={(event) =>
                    setPolicySettings((prev) => ({
                      ...prev,
                      returnCondition: event.target.value,
                    }))
                  }
                  style={{ border: "1px solid #cbd5e1", borderRadius: "6px", padding: "8px" }}
                >
                  <option value="unused_unopened">Unused and unopened</option>
                  <option value="unused">Unused condition</option>
                  <option value="damaged_only">Damaged items only</option>
                </select>
              </label>

              <label style={{ display: "grid", gap: "6px", fontSize: "12px" }}>
                Processing time (business days)
                <input
                  type="text"
                  value={policySettings.processingTimeBusinessDays}
                  onChange={(event) =>
                    setPolicySettings((prev) => ({
                      ...prev,
                      processingTimeBusinessDays: event.target.value,
                    }))
                  }
                  style={{ border: "1px solid #cbd5e1", borderRadius: "6px", padding: "8px" }}
                />
              </label>

              <label style={{ display: "grid", gap: "6px", fontSize: "12px" }}>
                Support contact (email or phone)
                <input
                  type="text"
                  value={policySettings.supportContact}
                  onChange={(event) =>
                    setPolicySettings((prev) => ({
                      ...prev,
                      supportContact: event.target.value,
                    }))
                  }
                  style={{ border: "1px solid #cbd5e1", borderRadius: "6px", padding: "8px" }}
                />
              </label>
            </div>

            <label style={{ display: "grid", gap: "6px", fontSize: "12px", marginBottom: "10px" }}>
              Non-refundable items
              <input
                type="text"
                value={policySettings.nonRefundableItems}
                onChange={(event) =>
                  setPolicySettings((prev) => ({
                    ...prev,
                    nonRefundableItems: event.target.value,
                  }))
                }
                style={{ border: "1px solid #cbd5e1", borderRadius: "6px", padding: "8px" }}
              />
            </label>

            <label style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px", fontSize: "13px" }}>
              <input
                type="checkbox"
                checked={policySettings.exchangeAllowed}
                onChange={(event) =>
                  setPolicySettings((prev) => ({
                    ...prev,
                    exchangeAllowed: event.target.checked,
                  }))
                }
              />
              Exchanges are offered for eligible items
            </label>

            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "12px" }}>
              <button
                type="button"
                onClick={handleGeneratePolicyDrafts}
                style={{
                  border: "1px solid #1d4ed8",
                  background: "#ffffff",
                  color: "#1d4ed8",
                  borderRadius: "8px",
                  padding: "8px 12px",
                  fontSize: "12px",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Generate Policy Drafts
              </button>
              <button
                type="button"
                onClick={handleSavePolicies}
                disabled={savingPolicies}
                style={{
                  border: "1px solid #1d4ed8",
                  background: savingPolicies ? "#bfdbfe" : "#2563eb",
                  color: "#ffffff",
                  borderRadius: "8px",
                  padding: "8px 12px",
                  fontSize: "12px",
                  fontWeight: 600,
                  cursor: savingPolicies ? "not-allowed" : "pointer",
                }}
              >
                {savingPolicies ? "Saving..." : "Save Policy Documents"}
              </button>
            </div>

            <label style={{ display: "grid", gap: "6px", fontSize: "12px", marginBottom: "10px" }}>
              Terms and Conditions
              <textarea
                rows={6}
                value={policyTerms}
                onChange={(event) => setPolicyTerms(event.target.value)}
                style={{ border: "1px solid #cbd5e1", borderRadius: "8px", padding: "10px", resize: "vertical" }}
              />
            </label>

            <label style={{ display: "grid", gap: "6px", fontSize: "12px", marginBottom: "10px" }}>
              Refund Policy
              <textarea
                rows={6}
                value={policyRefund}
                onChange={(event) => setPolicyRefund(event.target.value)}
                style={{ border: "1px solid #cbd5e1", borderRadius: "8px", padding: "10px", resize: "vertical" }}
              />
            </label>

            <label style={{ display: "grid", gap: "6px", fontSize: "12px" }}>
              Cancellation Policy
              <textarea
                rows={6}
                value={policyCancellation}
                onChange={(event) => setPolicyCancellation(event.target.value)}
                style={{ border: "1px solid #cbd5e1", borderRadius: "8px", padding: "10px", resize: "vertical" }}
              />
            </label>

            <p style={{ margin: "10px 0 0 0", fontSize: "11px", color: "#64748b" }}>
              Template text is provided to help you complete setup quickly and is
              not legal advice.
            </p>
          </div>

          <div
            style={{
              background: "#ffffff",
              border: "1px solid #e2e8f0",
              borderRadius: "12px",
              padding: "18px",
              marginTop: "16px",
            }}
          >
            <h3
              style={{
                margin: "0 0 12px 0",
                fontSize: "16px",
                color: "#0f172a",
              }}
            >
              Storefront Banner Image Configuration
            </h3>
            <p
              style={{
                margin: "0 0 12px 0",
                color: "#475569",
                fontSize: "13px",
              }}
            >
              Upload up to 3 storefront banner images used in your storefront
              hero section. To replace an older banner image, remove one below,
              then upload a new image.
            </p>

            <div style={{ marginTop: "12px" }}>
              <ImageUploadField
                label="Upload banner image file"
                buttonLabel="Choose banner image"
                uploadOnSelect={false}
                showPreviews={false}
                onSelectFiles={(file) => {
                  setHeroImageFile(file || null);
                  setSaveMsg("");
                }}
              />
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                <button
                  type="button"
                  onClick={handleHeroImageUpload}
                  disabled={
                    uploadingHeroImage ||
                    !heroImageFile ||
                    heroImages.length >= 3
                  }
                  style={{
                    border: "1px solid #1d4ed8",
                    background:
                      uploadingHeroImage ||
                      !heroImageFile ||
                      heroImages.length >= 3
                        ? "#bfdbfe"
                        : "#2563eb",
                    color: "#ffffff",
                    borderRadius: "8px",
                    padding: "8px 12px",
                    fontSize: "12px",
                    fontWeight: 600,
                    cursor:
                      uploadingHeroImage ||
                      !heroImageFile ||
                      heroImages.length >= 3
                        ? "not-allowed"
                        : "pointer",
                  }}
                >
                  {uploadingHeroImage ? "Uploading..." : "Upload image"}
                </button>
              </div>
              <p
                style={{
                  margin: "8px 0 0 0",
                  fontSize: "12px",
                  color: "#475569",
                }}
              >
                {heroImages.length}/3 images uploaded
              </p>
              {heroImages.length >= 3 && (
                <p
                  style={{
                    margin: "8px 0 0 0",
                    fontSize: "12px",
                    color: "#b45309",
                    fontWeight: 600,
                  }}
                >
                  Maximum reached: remove one image to upload a replacement.
                </p>
              )}
              {heroImageFile && (
                <p
                  style={{
                    margin: "8px 0 0 0",
                    fontSize: "12px",
                    color: "#334155",
                  }}
                >
                  Selected: {heroImageFile.name}
                </p>
              )}
            </div>

            {heroImages.length > 0 && (
              <div style={{ marginTop: "12px" }}>
                <div
                  style={{
                    fontSize: "12px",
                    color: "#334155",
                    marginBottom: "8px",
                  }}
                >
                  Uploaded storefront banner images
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns:
                      "repeat(auto-fill, minmax(160px, 1fr))",
                    gap: "10px",
                  }}
                >
                  {heroImages.map((imageUrl, index) => (
                    <div key={`${imageUrl}-${index}`}>
                      <img
                        src={imageUrl}
                        alt={`Storefront banner ${index + 1}`}
                        style={{
                          width: "100%",
                          height: "110px",
                          objectFit: "cover",
                          borderRadius: "10px",
                          border: "1px solid #e2e8f0",
                          background: "#f8fafc",
                        }}
                      />
                      <div
                        style={{
                          marginTop: "6px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: "8px",
                        }}
                      >
                        <span
                          style={{
                            fontSize: "11px",
                            color: index === 0 ? "#166534" : "#475569",
                            fontWeight: index === 0 ? 700 : 500,
                          }}
                        >
                          {index === 0 ? "Primary banner" : `Banner ${index + 1}`}
                        </span>
                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                          {index > 0 && (
                            <button
                              type="button"
                              onClick={() => handleSetPrimaryHeroImage(index)}
                              disabled={saving || uploadingHeroImage}
                              style={{
                                border: "1px solid #1d4ed8",
                                color: "#1d4ed8",
                                background: "#ffffff",
                                borderRadius: "6px",
                                padding: "4px 8px",
                                fontSize: "11px",
                                fontWeight: 600,
                                cursor:
                                  saving || uploadingHeroImage
                                    ? "not-allowed"
                                    : "pointer",
                                opacity: saving || uploadingHeroImage ? 0.6 : 1,
                              }}
                            >
                              Set as Primary
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => handleRemoveHeroImage(index)}
                            disabled={saving || uploadingHeroImage}
                            style={{
                              border: "1px solid #dc2626",
                              color: "#dc2626",
                              background: "#ffffff",
                              borderRadius: "6px",
                              padding: "4px 8px",
                              fontSize: "11px",
                              fontWeight: 600,
                              cursor:
                                saving || uploadingHeroImage
                                  ? "not-allowed"
                                  : "pointer",
                              opacity: saving || uploadingHeroImage ? 0.6 : 1,
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div
            style={{
              background: "#ffffff",
              border: "1px solid #e2e8f0",
              borderRadius: "12px",
              padding: "18px",
              marginTop: "16px",
            }}
          >
            <h3
              style={{
                margin: "0 0 12px 0",
                fontSize: "16px",
                color: "#0f172a",
              }}
            >
              Primary Categories Configuration
            </h3>
            <p
              style={{
                margin: "0 0 12px 0",
                color: "#475569",
                fontSize: "13px",
              }}
            >
              Selected categories are used to scope category options on product
              add pages for your role.
            </p>

            {availableCategories.length === 0 ? (
              <p style={{ margin: 0, color: "#64748b", fontSize: "13px" }}>
                No platform categories available.
              </p>
            ) : (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
                  gap: "10px",
                }}
              >
                {availableCategories.map((category) => {
                  const value = String(category?.value || "").trim();
                  const checked = selectedPrimaryCategories.some(
                    (entry) =>
                      String(entry).toLowerCase() === value.toLowerCase(),
                  );

                  return (
                    <label
                      key={value}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        padding: "10px",
                        borderRadius: "8px",
                        border: checked
                          ? "1px solid #2563eb"
                          : "1px solid #dbe3ee",
                        background: checked ? "#eff6ff" : "#ffffff",
                        cursor: "pointer",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => togglePrimaryCategory(value)}
                      />
                      <span style={{ fontSize: "13px", color: "#0f172a" }}>
                        {category?.icon ? `${category.icon} ` : ""}
                        {category?.label || value}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}

            <p
              style={{
                margin: "10px 0 0 0",
                fontSize: "12px",
                color: "#475569",
              }}
            >
              {selectedPrimaryCategories.length} selected
            </p>
          </div>
        </>
      )}
    </div>
  );

  if (isBusiness) {
    return <SidebarLayout>{content}</SidebarLayout>;
  }

  return <FreelancerSidebarLayout>{content}</FreelancerSidebarLayout>;
}

export default StorefrontSetupPage;
